import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Typography, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";

const { Dragger } = Upload;
const { Text } = Typography;

const DEFAULT_ALLOWED_FILE_TYPES = [
  "image/*",
  ".svg",
  ".pdf",
  ".woff",
  ".woff2",
  ".ttf",
];

const defaultNote = "Immagini, PDF, SVG, font (max 50 MB)";

const isFileTypeAllowed = (file, allowedFileTypes) => {
  const filename = file.name.toLowerCase();
  const mimeType = (file.type || "").toLowerCase();

  return allowedFileTypes.some((allowedType) => {
    const normalizedType = allowedType.toLowerCase();

    if (normalizedType.endsWith("/*")) {
      const prefix = normalizedType.replace("*", "");
      return mimeType.startsWith(prefix);
    }

    return filename.endsWith(normalizedType);
  });
};

const parseErrorResponse = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const uploadFileToSignedUrl = ({
  file,
  method,
  onProgress,
  url,
}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open(method || "PUT", url);
    if (file.type) {
      xhr.setRequestHeader("Content-Type", file.type);
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || typeof onProgress !== "function") {
        return;
      }

      onProgress({ percent: (event.loaded / event.total) * 100 });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }

      reject(new Error(`S3 upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => {
      reject(new Error("Network error during CDN upload"));
    };

    xhr.send(file);
  });

const CdnUploader = ({
  path = "",
  onComplete,
  allowedFileTypes = DEFAULT_ALLOWED_FILE_TYPES,
  maxFileSize = 50 * 1024 * 1024,
  maxNumberOfFiles = 5,
  note = defaultNote,
}) => {
  const [fileList, setFileList] = useState([]);
  const [conflictState, setConflictState] = useState({
    visible: false,
    filename: "",
    draftFilename: "",
  });

  const conflictResolverRef = useRef(null);
  const lastRejectedBatchRef = useRef("");
  const uploadQueueRef = useRef(Promise.resolve());

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  useEffect(() => {
    return () => {
      if (conflictResolverRef.current) {
        conflictResolverRef.current({ action: "cancel" });
        conflictResolverRef.current = null;
      }
    };
  }, []);

  const closeConflictDialog = (result) => {
    if (conflictResolverRef.current) {
      conflictResolverRef.current(result);
      conflictResolverRef.current = null;
    }

    setConflictState({
      visible: false,
      filename: "",
      draftFilename: "",
    });
  };

  const openConflictDialog = (filename) =>
    new Promise((resolve) => {
      conflictResolverRef.current = resolve;
      setConflictState({
        visible: true,
        filename,
        draftFilename: filename,
      });
    });

  const requestUploadTarget = async ({
    filename,
    contentType,
    overwrite = false,
    token,
  }) => {
    const response = await fetch(`${config.baseUrl[env]}/cdn-upload`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        filename,
        contentType,
        overwrite,
        path,
      }),
    });

    if (response.status === 409) {
      const payload = await parseErrorResponse(response);
      return {
        status: "conflict",
        error: payload?.error || {
          code: "FILE_ALREADY_EXISTS",
          filename,
        },
      };
    }

    if (!response.ok) {
      const payload = await parseErrorResponse(response);
      throw new Error(
        payload?.error?.message || `Server returned ${response.status}`
      );
    }

    const data = await response.json();

    if (!data.result?.url || !data.result?.s3key) {
      throw new Error("Invalid response from server");
    }

    return {
      status: "ready",
      result: data.result,
    };
  };

  const resolveUploadTarget = async ({ file, token }) => {
    let filename = file.name;
    let overwrite = false;

    while (true) {
      const uploadTarget = await requestUploadTarget({
        filename,
        contentType: file.type || "application/octet-stream",
        overwrite,
        token,
      });

      if (uploadTarget.status === "ready") {
        return {
          ...uploadTarget.result,
          filename,
          overwrite,
        };
      }

      const resolution = await openConflictDialog(
        uploadTarget.error?.filename || filename
      );

      if (resolution.action === "cancel") {
        const error = new Error("Upload cancelled by user");
        error.code = "UPLOAD_CANCELLED";
        throw error;
      }

      if (resolution.action === "override") {
        overwrite = true;
        continue;
      }

      filename = resolution.filename;
      overwrite = false;
    }
  };

  const customRequest = (options) => {
    const runUpload = async () => {
      const { file, onError, onProgress, onSuccess } = options;
      const rawFile = file.originFileObj || file;

      try {
        const token = await getTokenSilently();
        const uploadTarget = await resolveUploadTarget({
          file: rawFile,
          token,
        });

        await uploadFileToSignedUrl({
          file: rawFile,
          method: uploadTarget.method,
          onProgress,
          url: uploadTarget.url,
        });

        const publicUrl = `${config.cdn[env]}/${uploadTarget.s3key}`;

        if (uploadTarget.overwrite) {
          message.info(
            "Override completato: il CDN puo mostrare il file precedente per circa 2 minuti"
          );
        } else {
          message.success(`File caricato: ${uploadTarget.filename}`);
        }

        onSuccess(
          {
            uploadURL: publicUrl,
            s3key: uploadTarget.s3key,
          },
          rawFile
        );

        if (typeof onComplete === "function") {
          onComplete(publicUrl, uploadTarget.s3key);
        }
      } catch (error) {
        if (error.code === "UPLOAD_CANCELLED") {
          message.info("Upload annullato");
        } else {
          message.error(error.message || "Errore durante l'upload");
        }

        onError(error);
      }
    };

    const queuedUpload = uploadQueueRef.current.then(runUpload);
    uploadQueueRef.current = queuedUpload.catch(() => undefined);

    return {
      abort() {},
    };
  };

  const handleBeforeUpload = (file, incomingFileList = []) => {
    if (!isFileTypeAllowed(file, allowedFileTypes)) {
      message.error("Tipo file non supportato");
      return Upload.LIST_IGNORE;
    }

    if (file.size > maxFileSize) {
      message.error(
        `File troppo grande: massimo ${Math.round(maxFileSize / (1024 * 1024))} MB`
      );
      return Upload.LIST_IGNORE;
    }

    const knownFileIds = new Set(fileList.map((item) => item.uid));
    const newBatchFiles = incomingFileList.filter(
      (incomingFile) => !knownFileIds.has(incomingFile.uid)
    );

    if (fileList.length + newBatchFiles.length > maxNumberOfFiles) {
      const rejectedBatchKey = newBatchFiles.map((item) => item.uid).join("|");

      if (lastRejectedBatchRef.current !== rejectedBatchKey) {
        lastRejectedBatchRef.current = rejectedBatchKey;
        message.error(
          `Puoi caricare al massimo ${maxNumberOfFiles} file per volta`
        );
      }

      return Upload.LIST_IGNORE;
    }

    lastRejectedBatchRef.current = "";

    return true;
  };

  const handleRenameConfirm = () => {
    const trimmedFilename = conflictState.draftFilename.trim();

    if (!trimmedFilename) {
      message.error("Inserisci un nome file valido");
      return;
    }

    closeConflictDialog({
      action: "rename",
      filename: trimmedFilename,
    });
  };

  return (
    <>
      <Dragger
        multiple
        accept={allowedFileTypes.join(",")}
        customRequest={customRequest}
        beforeUpload={handleBeforeUpload}
        fileList={fileList}
        onChange={({ fileList: nextFileList }) => setFileList(nextFileList)}
        onRemove={(file) => {
          setFileList((currentFileList) =>
            currentFileList.filter((item) => item.uid !== file.uid)
          );
          return true;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Trascina qui il file oppure clicca per selezionarlo
        </p>
        <p className="ant-upload-hint">{note}</p>
      </Dragger>

      <Modal
        visible={conflictState.visible}
        title="File gia presente"
        onCancel={() => closeConflictDialog({ action: "cancel" })}
        footer={[
          <Button
            key="cancel"
            onClick={() => closeConflictDialog({ action: "cancel" })}
          >
            Annulla
          </Button>,
          <Button
            key="override"
            danger
            onClick={() => closeConflictDialog({ action: "override" })}
          >
            Override
          </Button>,
          <Button key="rename" type="primary" onClick={handleRenameConfirm}>
            Rinomina e carica
          </Button>,
        ]}
      >
        <p>
          Esiste gia un file chiamato <Text strong>{conflictState.filename}</Text>{" "}
          nella cartella corrente.
        </p>
        <p>
          Puoi sovrascriverlo oppure scegliere un nome diverso per il nuovo file.
        </p>
        <Input
          autoFocus
          value={conflictState.draftFilename}
          placeholder="Nuovo nome file"
          onChange={(event) =>
            setConflictState((currentState) => ({
              ...currentState,
              draftFilename: event.target.value,
            }))
          }
          onPressEnter={handleRenameConfirm}
        />
        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
          In caso di override, lo stesso URL puo restare in cache sul CDN per circa
          2 minuti.
        </Text>
      </Modal>
    </>
  );
};

export default CdnUploader;
